import { useEffect, useRef, useState } from 'react';
import { Images, SwitchCamera, X, Zap } from 'lucide-react';
import {
  captureError,
  cameraFacing,
  canFlipCamera,
  canRecord,
  clipFileName,
  clipLimitMs,
  firstFrame,
  flipCamera,
  lastFacing,
  MAX_VIDEO_NOTE_MS,
  openMicrophone,
  startCapture,
  startClip,
  type Capture,
  type ClipTake,
  type Facing,
  type Preview,
  type Recorded,
} from '../lib/capture.ts';
import {
  currentZoom,
  formatZoom,
  pinchZoom,
  zoomRange,
  zoomWorthShowing,
  type ZoomRange,
} from '../lib/cameraZoom.ts';
import { formatDuration } from '../lib/format.ts';
import { watchTilt } from '../lib/deviceTilt.ts';
import { holdNavigationBar } from '../lib/navigationBar.ts';
import { rotationFor, turnTowards, type Tilt } from '../lib/tilt.ts';
import { hasSecondCamera, openViewfinder, snapshot } from '../lib/viewfinder.ts';
import { distance, type Point } from '../lib/zoom.ts';
import { LiveCircle } from './RecordControl.tsx';
import './CameraScreen.css';

/**
 * The camera, full screen, with a shutter under it.
 *
 * This used to be a live tile inside the attach menu, and the tile is what it
 * outgrew rather than what it replaced. A viewfinder 148px tall can hold one
 * control — the shutter — and the moment the camera needs a flash, a flip, and a
 * choice between a photograph and a clip, the tile is a control panel wearing a
 * picture. Given a screen, all four are thumb-sized and the picture is the size
 * of the thing being framed, which is what a camera is for.
 *
 * A photograph and a clip leave as a File and go to the same place a picked one
 * does — the album, then the compose screen — so nothing downstream has a branch
 * for "this was filmed here": see addToAlbum in Composer.tsx, which grabs a
 * poster and a duration off a clip the same way whether it came from the gallery
 * or from this screen.
 *
 * The third mode does not: a кружочек is its own kind of message, with no
 * caption and no grouping, so «Кружочек» hands back a Recorded and the caller
 * takes it to the same trim screen a composer take ends on. What this mode adds
 * over holding the composer's button is time *before* the take — the circle is
 * live and the camera flippable while nothing is recorded yet, and recording
 * starts on its own tap. The recording itself is the composer's exact path,
 * startCapture: same square encode, same native recorder inside the iOS app,
 * same file either way — which is why the viewfinder's own stream is let go
 * first and the circle then shows the take's camera, not a second one.
 *
 * There is no strip of recent shots, which the handoff draws under the
 * viewfinder. A page cannot enumerate a phone's camera roll — the same wall the
 * in-app gallery grid hit, see the README — so the strip could only ever show
 * this session's own takes, and the flow it would belong to is not the one the
 * handoff specifies: the shutter goes straight to the compose screen.
 */

type Mode = 'photo' | 'video' | 'note';
type State = 'opening' | 'live' | 'error';

/** How often the recording clock is redrawn. */
const TICK_MS = 200;

/**
 * The last mode survives the screen: a camera opened five times a day for the
 * same thing should not start from «Фото» five times. The handoff names the key.
 */
const MODE_KEY = 'chat.camera.lastMode';

function lastMode(): Mode {
  try {
    const stored = localStorage.getItem(MODE_KEY);
    return stored === 'photo' || stored === 'video' || stored === 'note' ? stored : 'photo';
  } catch {
    return 'photo';
  }
}

export function CameraScreen({
  maxUploadBytes,
  photoOnly = false,
  onCapture,
  onNote,
  onGallery,
  onSystemCamera,
  onClose,
}: {
  /** The server's per-file cap, which is what bounds how long a clip may run. */
  maxUploadBytes: number;
  /**
   * Hides the Видео/Фото switch and leaves the screen on photo. For the caller
   * that has nowhere to put a clip — an avatar — where offering the mode would
   * only be a way to reach a shutter that cannot finish.
   */
  photoOnly?: boolean;
  /** A photograph or a clip, ready to join the album like any picked file. */
  onCapture: (file: File) => void;
  /**
   * A finished кружочек, bound for the trim screen rather than the album.
   * Absent for callers with nowhere to send one — an avatar, the dev preview
   * without a conversation — and the «Кружочек» mode is not offered without it.
   */
  onNote?: (recorded: Recorded) => void;
  /** Leaves for the system picker — the shortcut in the control row. */
  onGallery: () => void;
  /**
   * The phone's own camera app, through an `<input capture>`: the fallback for a
   * device that will not hand this page a stream. Null on a desktop, where
   * `capture` means nothing and the button would open a file picker instead.
   */
  onSystemCamera: (() => void) | null;
  onClose: () => void;
}) {
  /** Whether «Кружочек» is offered at all: somewhere to send one, a browser
      that can record one, and not the photo-only screen. */
  const noteable = !photoOnly && onNote !== undefined && canRecord();

  const [state, setState] = useState<State>('opening');
  const [error, setError] = useState('');
  const [mode, setMode] = useState<Mode>(() => {
    if (photoOnly) return 'photo';
    const remembered = lastMode();
    return remembered === 'note' && !noteable ? 'photo' : remembered;
  });
  /** Only the self-view's mirror reads this; the preference itself is capture.ts's. */
  const [facing, setFacing] = useState<Facing>(mode === 'note' ? cameraFacing() : lastFacing());
  const [flippable, setFlippable] = useState(false);
  const [torchable, setTorchable] = useState(false);
  const [torch, setTorch] = useState(false);
  /** What the open camera's track can zoom through; null hides the pinch. */
  const [zoomable, setZoomable] = useState<ZoomRange | null>(null);
  const [zoom, setZoom] = useState(1);
  /** Two fingers are down: the pill shows even at 1×, as the pinch's answer. */
  const [pinching, setPinching] = useState(false);
  /** True while a frame is being encoded — a double tap must not send two. */
  const [taking, setTaking] = useState(false);
  /**
   * Which way the phone is held, from the accelerometer — the page itself
   * never turns (both shells are portrait-only, and so is a browser under the
   * rotation lock), so this is the only way a sideways photograph comes out
   * upright. `spin` is the same fact as a CSS angle for the icons, kept
   * unbounded so a change of quadrant animates the short way round.
   */
  const [tilt, setTilt] = useState<Tilt>(0);
  const [spin, setSpin] = useState(0);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  /** Set once a take starts without a microphone, so the screen can say so. */
  const [silent, setSilent] = useState(false);

  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  /** Held across takes while the mode is video; null in photo mode. See below. */
  const microphone = useRef<MediaStream | null>(null);
  const take = useRef<ClipTake | null>(null);
  /** The running кружочек — the composer's own kind of take, not a ClipTake. */
  const note = useRef<Capture | null>(null);
  /** The note take's camera, once it has opened; the circle shows this. */
  const [notePreview, setNotePreview] = useState<Preview | null>(null);
  const clock = useRef<number | undefined>(undefined);
  /** Whether this screen is still up, since opening a camera is awaited. */
  const alive = useRef(true);
  /** Every finger currently down, by pointer id — two of them is a pinch. */
  const fingers = useRef(new Map<number, Point>());
  /** The spread and the zoom the pinch began at; every move measures against them. */
  const pinchStart = useRef<{ spread: number; zoom: number } | null>(null);
  /** The zoom as the handlers know it, which run between renders. */
  const zoomNow = useRef(1);
  /** One applyConstraints in flight at a time; the latest ask wins. */
  const zoomBusy = useRef(false);
  const zoomQueued = useRef<number | null>(null);

  const limitMs = clipLimitMs(maxUploadBytes);

  /** Lets go of the camera, and of its indicator light with it. */
  const stop = () => {
    for (const track of stream.current?.getTracks() ?? []) track.stop();
    stream.current = null;
  };

  const start = async (want: Facing) => {
    setState('opening');
    setTorch(false);
    try {
      let opened: MediaStream;
      try {
        opened = await openViewfinder(want);
      } catch (failure) {
        // Some phones cannot hold both cameras open at once and refuse the
        // polite order — new one first, old one after. Letting go and asking
        // again is the only way round it; capture.ts does the same on a flip
        // mid-recording.
        if (!(failure instanceof DOMException) || failure.name !== 'NotReadableError') throw failure;
        stop();
        opened = await openViewfinder(want);
      }

      // Closed while the camera was opening: nothing here has anywhere to go,
      // and the stream has to be released or the light stays on over a screen
      // that is no longer up.
      if (!alive.current) {
        for (const track of opened.getTracks()) track.stop();
        return;
      }

      stop();
      stream.current = opened;
      const element = video.current;
      if (element) {
        element.srcObject = opened;
        // A play() that loses a race with the next flip rejects; there is
        // nothing to recover and nothing to tell anyone.
        await element.play().catch(() => {});
        // And then for a frame of it. Until one arrives the element is still
        // painting the camera that was here before, and the mirror below turns
        // with `facing` — set it any earlier and a flip is the old picture
        // flipping first and the new camera arriving after. See firstFrame.
        await firstFrame(element);
      }

      // Closed while that frame was awaited. The stream is this function's until
      // the screen is told about it, so it is this function that lets it go —
      // the unmount already ran and stopped whatever it could see, which was not
      // this.
      if (!alive.current) {
        stop();
        return;
      }

      setFacing(want);
      setState('live');

      // Asked of the track that is actually open, not of the browser: a torch is
      // the back camera of an Android phone and almost nothing else, and a flash
      // button over a laptop webcam is a control that does nothing. The zoom is
      // read the same way and for the same reason — and re-read here on every
      // flip, because the other camera is a different lens with a different
      // range, starting from wherever its track opens.
      const camera = opened.getVideoTracks()[0];
      const capabilities = camera?.getCapabilities?.() as { torch?: boolean } | undefined;
      setTorchable(capabilities?.torch === true);
      const range = camera ? zoomRange(camera) : null;
      setZoomable(range);
      const level = range && camera ? currentZoom(camera, range) : 1;
      zoomNow.current = level;
      setZoom(level);
      pinchStart.current = null;
      setPinching(false);

      void hasSecondCamera().then((second) => {
        if (alive.current) setFlippable(second);
      });
    } catch (failure) {
      stop();
      if (!alive.current) return;
      setError(captureError(failure));
      setState('error');
    }
  };

  /**
   * Which camera a given mode opens on: the one last used, whichever mode used
   * it. Turning the camera round is a decision about where the phone is being
   * pointed rather than about what is being shot, so it outlives both the mode
   * and the screen — see flipCamera, which writes it down.
   *
   * The кружочек reads through the gate rather than raw, and that is the whole
   * difference between the two: its take is filmed by startCapture on its own
   * camera, which asks `cameraFacing()` — so with the flip switched off at
   * /admin that take is always the front one, and a viewfinder honouring a
   * remembered `environment` would be framing a shot the recorder will not
   * film. Photo and video answer to this screen's own flip button, which is
   * gated on there being a second camera and nothing else.
   */
  const facingFor = (which: Mode): Facing => (which === 'note' ? cameraFacing() : lastFacing());

  useEffect(() => {
    alive.current = true;
    // Straight in, without the cameraGranted() check the attach menu used to
    // make: opening a *menu* is not consent to be asked for a permission, but
    // opening this screen is nothing but that request.
    void start(facingFor(mode));
    return () => {
      alive.current = false;
      window.clearInterval(clock.current);
      take.current?.cancel();
      take.current = null;
      note.current?.cancel();
      note.current = null;
      stop();
    };
    // Mount and unmount only: the camera is restarted by a flip, and re-running
    // this on one would fight the flip for the stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The microphone follows the mode, not the shutter.
   *
   * Asking for it inside the take — which is what startClip used to do — puts a
   * permission dialog on top of the picture at the exact moment something is
   * being filmed: the opening seconds go to the prompt, and answering it a beat
   * late produces a silent clip of the thing that was worth filming. Switching to
   * «Видео» is the moment the sender says they want sound, so that is where the
   * question goes, and the answer is held: a second clip asks nothing and starts
   * on the frame the shutter was pressed on.
   *
   * A refusal is not an error here. It leaves the ref null, the take runs
   * silently, and the screen says so while it is still worth saying.
   */
  useEffect(() => {
    if (mode !== 'video') return;
    let stale = false;

    void openMicrophone()
      .then((opened) => {
        if (stale || !alive.current) {
          for (const track of opened.getTracks()) track.stop();
          return;
        }
        microphone.current = opened;
      })
      .catch((failure: unknown) => {
        console.warn('camera: filming without sound', failure);
      });

    // Back to photographs, or off the screen: nothing is being filmed, so
    // nothing should be listening.
    return () => {
      stale = true;
      for (const track of microphone.current?.getTracks() ?? []) track.stop();
      microphone.current = null;
    };
  }, [mode]);

  // Escape closes it, as it closes every other screen in the app. Not while a
  // take is running, though: that would be a keystroke throwing away a
  // recording, which is what the stop button is for.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !recording) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, recording]);

  const toggleTorch = () => {
    const track = stream.current?.getVideoTracks()[0];
    if (!track) return;
    const next = !torch;
    // Optimistic, and correcting itself: a torch that refuses to come on is
    // worth showing as off rather than as an error over a working camera.
    setTorch(next);
    // `torch` is not in lib.dom, because it is not in the spec every browser
    // implements — which is the same reason the button is only drawn when the
    // track claims the capability above.
    const constraints = { advanced: [{ torch: next }] } as unknown as MediaTrackConstraints;
    void track.applyConstraints(constraints).catch(() => setTorch(!next));
  };

  // Nothing here touches `facing`: the mirror it drives turns inside start, once
  // the new camera has actually put a frame on screen.
  const flip = () => {
    void start(flipCamera());
  };

  /** Ends a clip and sends it the way of every other attachment. */
  const finish = async () => {
    const running = take.current;
    if (!running) return;
    take.current = null;
    window.clearInterval(clock.current);
    setRecording(false);
    setTaking(true);
    try {
      const clip = await running.stop();
      if (!alive.current) return;
      onCapture(new File([clip.blob], clipFileName(clip.mime), { type: clip.mime }));
    } catch (failure) {
      if (!alive.current) return;
      setError(captureError(failure));
      setState('error');
    } finally {
      setTaking(false);
    }
  };

  /**
   * Whether a pinch would be honest right now. It is a question about the
   * camera rather than about the mode: whichever camera this screen is showing
   * has to be one the fingers can actually move, and a кружочек is filmed by
   * two different cameras over its life — the viewfinder's until the shutter,
   * the take's own afterwards. Both zoom, by different means; `taking` is the
   * seam between them, where neither is ours to touch yet.
   */
  const pinchable = zoomable !== null && state === 'live' && !taking;

  /**
   * Zoom on the camera, not on the picture, so a photograph, a clip and a
   * кружочек are all taken through the lens as it is seen.
   *
   * Which camera that is changes mid-кружочек. Up to the shutter it is the
   * viewfinder's own track and a constraint on it; from the shutter it is the
   * take's — the viewfinder has let go of the device by then — and the take is
   * asked instead, which inside the iOS app is an AVCaptureDevice's
   * videoZoomFactor rather than a constraint at all. The number is the same on
   * both sides of the seam, which is what carries the framing across it.
   *
   * One request in flight at a time and the latest wins — a pinch produces
   * moves faster than a camera answers them, and queueing every one would have
   * the picture chasing the fingers seconds behind. The take's own path needs
   * none of that: it is already fire-and-forget on the other side.
   */
  const applyZoom = (next: number) => {
    zoomNow.current = next;
    setZoom(next);

    const running = note.current;
    if (running) {
      running.setZoom?.(next);
      return;
    }

    const track = stream.current?.getVideoTracks()[0];
    if (!track) return;
    if (zoomBusy.current) {
      zoomQueued.current = next;
      return;
    }
    zoomBusy.current = true;
    // `zoom` is not in lib.dom for the same reason `torch` is not.
    const constraints = { advanced: [{ zoom: next }] } as unknown as MediaTrackConstraints;
    void track
      .applyConstraints(constraints)
      .catch(() => {})
      .finally(() => {
        zoomBusy.current = false;
        if (zoomQueued.current !== null) {
          const queued = zoomQueued.current;
          zoomQueued.current = null;
          applyZoom(queued);
        }
      });
  };

  // Touch only, deliberately: a pinch is the phone's own word for this, and on
  // a desktop the mouse already has nothing to pinch with.
  const onPinchDown = (event: React.PointerEvent) => {
    if (event.pointerType !== 'touch') return;
    fingers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (fingers.current.size === 2 && pinchable) {
      const [a, b] = [...fingers.current.values()];
      pinchStart.current = { spread: distance(a!, b!), zoom: zoomNow.current };
      setPinching(true);
    }
  };

  const onPinchMove = (event: React.PointerEvent) => {
    if (!fingers.current.has(event.pointerId)) return;
    fingers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const started = pinchStart.current;
    if (!started || fingers.current.size < 2 || !zoomable || !pinchable) return;
    const [a, b] = [...fingers.current.values()];
    applyZoom(pinchZoom(started.zoom, started.spread, distance(a!, b!), zoomable));
  };

  const onPinchUp = (event: React.PointerEvent) => {
    fingers.current.delete(event.pointerId);
    if (fingers.current.size < 2) {
      pinchStart.current = null;
      setPinching(false);
    }
  };

  /**
   * Ends a кружочек and hands it to the trim screen. Its own ending rather than
   * a branch in `finish`, because nothing about a ClipTake applies: the bytes
   * leave as a Recorded, not a File, and the destination is the caller's trim
   * screen rather than the album.
   */
  const finishNote = async () => {
    const running = note.current;
    if (!running) return;
    note.current = null;
    window.clearInterval(clock.current);
    setRecording(false);
    setNotePreview(null);
    setTaking(true);
    try {
      const recorded = await running.stop();
      if (!alive.current) return;
      // A tap-tap too quick to have caught anything. The composer drops these
      // at the same threshold; here the camera comes back for another go
      // rather than the screen closing over nothing.
      if (recorded.durationMs < 700) {
        void start(facing);
        return;
      }
      onNote?.(recorded);
    } catch (failure) {
      if (!alive.current) return;
      setError(captureError(failure));
      setState('error');
    } finally {
      setTaking(false);
    }
  };

  /**
   * Starts a кружочек — the same startCapture the composer's hold starts, which
   * is the point: same square encode, same cap, same native recorder inside the
   * iOS app. The viewfinder's camera is let go first, because startCapture opens
   * its own and phones will not hold the same camera open twice; the frozen last
   * frame covers the gap until the take's picture lands in the circle.
   */
  const beginNote = async () => {
    setTaking(true);
    stop();
    try {
      // The cap stops the recorder by itself; this carries what it caught on to
      // the trim screen rather than leaving a clock counting past a take that
      // already ended. The zoom rides along: the take opens its own camera, and
      // the framing the pinch just set has to be the framing that is filmed.
      const started = await startCapture(
        'video',
        () => void finishNote(),
        zoomable && zoomWorthShowing(zoomNow.current) ? zoomNow.current : undefined,
      );
      if (!alive.current) return void started.cancel();
      note.current = started;
      setNotePreview(started.preview);
      setRecording(true);
      setElapsed(0);
      // The same buzz as the composer's hold, for the same reason: the sender is
      // watching the circle, not the button.
      navigator.vibrate?.(20);
      const at = performance.now();
      clock.current = window.setInterval(() => setElapsed(performance.now() - at), TICK_MS);
    } catch (failure) {
      if (!alive.current) return;
      setError(captureError(failure));
      setState('error');
    } finally {
      setTaking(false);
    }
  };

  /**
   * The switcher writes the preference down as it is used, so the next opening
   * lands on it. Turning to «Кружочек» also turns the camera to the front —
   * turning away leaves whatever camera is up, per the handoff.
   */
  const switchMode = (next: Mode) => {
    setMode(next);
    try {
      localStorage.setItem(MODE_KEY, next);
    } catch {
      // A browser without storage forgets the mode, and loses nothing else.
    }
    // Only when the two disagree, which is only ever the кружочек reading
    // through the /admin gate that photo and video do not — see facingFor.
    const wanted = facingFor(next);
    if (wanted !== facing) {
      void start(wanted);
      return;
    }
  };

  // For as long as the screen is up, in every mode: the Android navigation
  // bar sits over the bottom of the page, and three system buttons under the
  // shutter are what the phone's own camera never shows.
  useEffect(() => holdNavigationBar(), []);

  useEffect(
    () =>
      watchTilt((next) => {
        setTilt(next);
        // The icons turn against the phone, so they stay upright in the world:
        // a phone turned anticlockwise wants its icons turned clockwise, and a
        // CSS rotate's positive angle is clockwise — so the tilt goes in as it
        // is, not negated. (It was negated once, and the icons then stood on
        // their heads exactly as the photographs did.)
        setSpin((current) => turnTowards(current, next));
      }),
    [],
  );

  const shutter = async () => {
    if (state !== 'live' || taking) return;
    if (recording) return void (mode === 'note' ? finishNote() : finish());

    if (mode === 'note') return void beginNote();

    const element = video.current;
    if (!element) return;

    if (mode === 'photo') {
      // Freeze the viewfinder on the frame that is being taken, before anything
      // is awaited. Encoding the frame takes long enough on a phone to be seen
      // — most of a second — and a viewfinder that keeps moving through it
      // says the tap did not register, which is when every other button gets
      // tried. A paused element holds the last frame it drew, and snapshot's
      // drawImage runs synchronously against that same frame, so what is
      // frozen is what will land in the album. Nothing resumes it on success:
      // every caller closes the screen on capture, and a frame of live video
      // between the shot and the compose screen would only put the doubt back.
      element.pause();
      setTaking(true);
      try {
        onCapture(await snapshot(element, rotationFor(tilt, facing)));
      } catch (failure) {
        if (!alive.current) return;
        // Back to live behind the message: an error over a still frame reads as
        // a camera that died, and a retry from here films that same frame.
        void element.play().catch(() => {});
        setError(captureError(failure));
        setState('error');
      } finally {
        setTaking(false);
      }
      return;
    }

    const track = stream.current?.getVideoTracks()[0];
    if (!track) return;
    setTaking(true);
    try {
      // The clock starts when there is something to count — the same rule the
      // recording pill follows, and for the same reason: a timer running over
      // nothing reads as a recording that did not register.
      const started = await startClip(track, microphone.current, limitMs, () => void finish());
      if (!alive.current) return void started.cancel();
      take.current = started;
      setSilent(!started.sound);
      setRecording(true);
      setElapsed(0);
      const at = performance.now();
      clock.current = window.setInterval(() => setElapsed(performance.now() - at), TICK_MS);
    } catch (failure) {
      if (!alive.current) return;
      setError(captureError(failure));
      setState('error');
    } finally {
      setTaking(false);
    }
  };

  const shutterLabel =
    mode === 'photo'
      ? 'Сделать фото'
      : recording
        ? 'Остановить запись'
        : mode === 'note'
          ? 'Начать запись'
          : 'Записать видео';

  // There is no line of words under the shutter. It said what the shutter was
  // about to do — «Тап начинает запись», «Проверьте кадр» — which is a caption
  // on a button that a round red button already says, and it said it on the
  // screen with the least room for anything but the picture. The same words are
  // still on the button's own title and aria-label, where they reach whoever
  // cannot read the shape.

  // The icons turn with the phone only where the file will: a clip is
  // MediaRecorder's and stays portrait whatever the phone does, and icons that
  // turned for it would be promising a landscape the file does not deliver.
  const iconTurn = mode === 'photo' ? spin : turnTowards(spin, 0);

  return (
    <div
      className="camera-screen"
      role="dialog"
      aria-label="Камера"
      style={{ '--tilt': `${iconTurn}deg` } as React.CSSProperties}
    >
      <div className="camera-header">
        <button
          type="button"
          className="camera-round"
          onClick={onClose}
          disabled={recording}
          aria-label="Закрыть"
        >
          <X size={16} />
        </button>
        {torchable && (
          <button
            type="button"
            className={`camera-round${torch ? ' camera-round-on' : ''}`}
            onClick={toggleTorch}
            aria-label="Вспышка"
            aria-pressed={torch}
          >
            <Zap size={16} />
          </button>
        )}
      </div>

      <div
        className={`camera-view${mode === 'note' ? ' camera-view-note' : ''}`}
        // The whole frame is the pinch surface, buttons excluded by geometry:
        // everything pressable lives outside this div. touch-action in the CSS
        // is what stops the browser claiming the same two fingers for the page.
        onPointerDown={onPinchDown}
        onPointerMove={onPinchMove}
        onPointerUp={onPinchUp}
        onPointerCancel={onPinchUp}
      >
        {/* Mounted whatever the state, so the ref exists by the time a stream
            arrives; it draws nothing until one does. In note mode it is the
            circle — the same picture behind a round mask — until the take's own
            camera replaces it below. Its stream is stopped by then, so what it
            would show is a frozen frame; it steps aside instead. */}
        <video
          ref={video}
          className={`camera-video${notePreview ? ' camera-video-off' : ''}`}
          data-facing={facing}
          muted
          playsInline
          autoPlay
        />

        {/* The кружочек while it is recording: the take's camera, mounted the
            same way the composer's floating circle mounts it — or, inside the
            iOS app, an empty hole the native layer is aimed at. */}
        {notePreview && (
          <div className="camera-note-live">
            <LiveCircle source={notePreview} facing={facing} />
          </div>
        )}

        {/* The blink of a shutter, once, over the frozen frame: with the
            picture no longer moving it is the one instant that says «taken»
            rather than «stuck» — see shutter. */}
        {taking && mode === 'photo' && <div className="camera-blink" aria-hidden="true" />}

        {state === 'opening' && <p className="camera-note">Включаем камеру…</p>}

        {state === 'error' && (
          <div className="camera-failed">
            <p className="camera-note">{error}</p>
            {onSystemCamera && (
              <button type="button" className="camera-fallback" onClick={onSystemCamera}>
                Открыть камеру телефона
              </button>
            )}
          </div>
        )}

        {recording && (
          <div className="camera-clock">
            <span className="camera-dot" />
            {formatDuration(elapsed)}
            {/* A кружочек has a ceiling and the clock says so — the same rule as
                the composer's pill. A clip's cap depends on the upload limit and
                is minutes away; naming it here would only add a number. */}
            {mode === 'note' && (
              <span className="camera-cap"> / {formatDuration(MAX_VIDEO_NOTE_MS)}</span>
            )}
          </div>
        )}

        {/* Said while it is still worth saying: after the take it would be a
            note about a file that is already in the album. */}
        {recording && silent && <p className="camera-silent">Без звука — нет доступа к микрофону</p>}

        {/* The number, small and out of the way: while two fingers are down it
            answers the pinch, and it stays only while the zoom is not 1× —
            which is also all the reminder a later shot needs that the lens is
            not at rest. Bottom-centre puts it under the кружочек circle rather
            than on it; on the full-frame modes it sits on the picture's quiet
            edge. It steps aside for the no-sound notice, which is the rarer and
            louder of the two. */}
        {zoomable && (pinching || zoomWorthShowing(zoom)) && (
          <div
            className={`camera-zoom${recording && silent ? ' camera-zoom-raised' : ''}`}
            aria-hidden="true"
          >
            {formatZoom(zoom)}
          </div>
        )}
      </div>

      {/* Above the shutter rather than under it, per the handoff: below the
          shutter there is only the hint and the inset for the system's own
          navigation, whose buttons the labels used to land under. */}
      {!photoOnly && (
        <div className="camera-modes" role="tablist" aria-label="Режим съёмки">
          {(noteable ? (['photo', 'video', 'note'] as const) : (['photo', 'video'] as const)).map(
            (option) => (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={mode === option}
                className={`camera-mode${mode === option ? ' camera-mode-on' : ''}`}
                onClick={() => switchMode(option)}
                disabled={recording || taking}
              >
                {option === 'photo' ? 'Фото' : option === 'video' ? 'Видео' : 'Кружочек'}
              </button>
            ),
          )}
        </div>
      )}

      <div className="camera-controls">
        <button
          type="button"
          className="camera-square"
          onClick={onGallery}
          disabled={recording}
          aria-label="Галерея"
        >
          <Images size={18} />
        </button>

        <button
          type="button"
          className={`camera-shutter${recording ? ' camera-shutter-rec' : ''}${
            taking && mode === 'photo' ? ' camera-shutter-taking' : ''
          }`}
          onClick={() => void shutter()}
          disabled={state !== 'live' || taking}
          aria-label={shutterLabel}
          title={shutterLabel}
        />

        {/* Never mid-take: the encoder is fed by this very track, and swapping it
            out from under one ends the recording rather than turning it round.
            startVideoNote can flip mid-кружочек because a canvas sits between the
            two; there is deliberately no canvas here.

            Kept in the row when there is only one camera, rather than dropped:
            it is what holds the shutter in the middle of the screen, and a
            shutter that moves depending on the device is worse than an empty
            corner. */}
        <button
          type="button"
          className={`camera-square${flippable ? '' : ' camera-square-empty'}`}
          onClick={flip}
          // Also while one swap is still opening: a camera takes long enough to
          // be tapped through twice, and the second `start` would stop the
          // stream the first had just adopted. The кружочек drops the second tap
          // the same way — see flip in lib/useRecorder.ts.
          //
          // Note mode gates on canFlipCamera besides: startCapture opens the
          // camera capture.ts prefers, and with the flip switched off at /admin
          // that is always the front one — a viewfinder turned to the back
          // camera would be framing a take the recorder will not film.
          disabled={
            recording ||
            taking ||
            !flippable ||
            state !== 'live' ||
            (mode === 'note' && !canFlipCamera())
          }
          aria-label="Сменить камеру"
          aria-hidden={!flippable}
          tabIndex={flippable ? undefined : -1}
        >
          <SwitchCamera size={18} />
        </button>
      </div>
    </div>
  );
}
